import React, { Component, ReactNode } from 'react';
import { ListColumnField } from '../../../domain/list/ListDomains';
import { User } from '../../../domain/rbac/RbacDomains';
import { RootState } from '../../../redux/store';
import { ListState } from '../../../redux/reducers/ListReducer';
import { changeList, changeListData, changeListIsUpdate } from '../../../redux/actions/ListActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import MessageUtil from '../../../util/MessageUtil';
import DataTypeSelectBoxComponent from '../../custom/DataTypeSelectBoxComponent';
import DataType from '../../../constant/DataType';
import FormicaInput from 'components/custom/FormicaInput';
import RoleHelper from '../../../RoleHelper';
import { isStringValid } from '../../../util/Generator';
import { VariableProperties } from '../../../domain/datatype/VariablePropertiesDomains';
import { ComponentState, setSelectedItems } from '../../../redux/actions/ComponentActions';
import { AnimatedButton } from '@trlogic/ui-pack';
import CollectionNames from '../../../constant/CollectionNames';
import { ComponentDependency } from '../../../domain/ui/ComponentMetadata';
import { query, repository } from '@trlogic/client-js';
import and = query.and;
import eq = query.eq;
import QueryBuilder = query.QueryBuilder;
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import ComponentName from '../../../constant/ComponentName';
import LanguageUtil from '../../../util/LanguageUtil';

class ListVariableCreatorComponent extends Component<Props, State> {
  public state: State = new State();

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const { name, description, properties } = this.state;
    return (
      <div className={'w-full flex flex-col lg:flex-row justify-around items-center gap-x-3 '}>
        <FormicaInput
          value={name}
          onChange={(e: any) => this.onChangeVariableName(e)}
          name={'name'}
          placeholder={LanguageUtil.get({ id: 'common.input.placeholder.name', defaultMessage: 'Name' })}
          type={'text'}
          inputSize={'sm'}
          disabled={!RoleHelper.canEdit('List')}
          data-testid={`${ComponentName.LIST}_variable_creator_name_input`}
        />
        <DataTypeSelectBoxComponent
          disabled={!RoleHelper.canEdit('List')}
          onChangeValue={(item) => this.setState({ properties: item.properties })}
          properties={properties}
          structEnabled={false}
          dateEnabled={false}
          listEnabled={false}
          placeholder={LanguageUtil.get({ id: 'common.input.placeholder.dataType', defaultMessage: 'Data Type' })}
          data-testid={`${ComponentName.LIST}_variable_creator_datatype_selectbox`}
        />
        <FormicaInput
          value={description}
          name={LanguageUtil.get({ id: 'common.input.placeholder.description', defaultMessage: 'Description' })}
          onChange={(e: React.FormEvent<HTMLInputElement>) => this.setState({ description: (e.target as HTMLInputElement).value })}
          placeholder={LanguageUtil.get({ id: 'common.input.placeholder.description', defaultMessage: 'Description' })}
          type={'text'}
          inputSize={'sm'}
          disabled={!RoleHelper.canEdit('List')}
          data-testid={`${ComponentName.LIST}_variable_creator_description_input`}
        />
        {this.getListVariableActionButton()}
      </div>
    );
  }

  private getListVariableActionButton = (): ReactNode => {
    const {
      component: { selectedComponentIds },
    } = this.props;
    if (selectedComponentIds && selectedComponentIds.length > 0) {
      return (
        <AnimatedButton
          text={LanguageUtil.get({ id: 'common.button.delete', defaultMessage: 'Delete' })}
          customType={'secondary'}
          customSize={'sm'}
          className={'w-[400px]'}
          icon={'delete'}
          onClick={this.onRemoveAll}
          disabled={!RoleHelper.canEdit('List')}
          data-testid={`${ComponentName.LIST}_variable_creator_delete_button`}
        />
      );
    }
    return (
      <AnimatedButton
        text={LanguageUtil.get({ id: 'common.button.add', defaultMessage: 'Add' })}
        customType={'primary'}
        customSize={'sm'}
        className={'w-[400px]'}
        icon={'add'}
        onClick={this.addColumnField}
        disabled={!RoleHelper.canEdit('List')}
        data-testid={`${ComponentName.LIST}_variable_creator_add_button`}
      />
    );
  };

  private addColumnField = () => {
    const { name, description, properties, key } = this.state;
    const {
      list: { list },
      changeList,
      user,
    } = this.props;
    if (list.stage != 'DRAFT') {
      MessageUtil.warning({
        content: (
          <div data-testid={`${ComponentName.LIST}_variable_add_deploy_error`}>
            {LanguageUtil.get({ id: 'messages.warning.cantAddColumnToDeployList', defaultMessage: "You can't add column to deploy list." })}
          </div>
        ),
      });
      return;
    }
    const isExist = list.fields.some((f) => f.name == name);
    if (isExist) {
      MessageUtil.alreadyExist(LanguageUtil.get({ id: 'messages.columnName', defaultMessage: 'Column Name' }));
      return;
    }
    if (name && properties) {
      const currentField: ListColumnField = {
        name,
        type: properties.dataType || DataType.STRING,
        key,
        description,
        createdBy: user?.username,
      };
      list.fields.push(currentField);
      changeList(list);
      this.resetState();
    }
  };

  private resetState = () => {
    this.setState({
      name: '',
      description: '',
      key: false,
      properties: undefined,
    });
  };

  private onChangeVariableName = (e: any) => {
    if (!isStringValid(e.currentTarget.value)) {
      MessageUtil.warning({
        content: LanguageUtil.get({ id: 'messages.warning.invalidName', defaultMessage: 'Invalid Name' }),
      });
      return;
    } else {
      this.setState({ name: e.currentTarget.value });
    }
  };

  private onRemoveAll = (e: any) => {
    customConfirm({
      target: e.currentTarget,
      message: LanguageUtil.get({ id: 'messages.confirm.removeFields', defaultMessage: 'Are you sure remove this field(s)?' }),
      accept: () => {
        this.removeAll();
      },
    });
  };

  private removeAll = async () => {
    const {
      list: { list },
      changeList,
      setSelectedItems,
      component: { selectedComponentIds },
    } = this.props;
    if (selectedComponentIds.length > 0) {
      const excluded = ['item', 'description', 'expireAt', 'createdAt', 'lastUpdated'];
      const fields = (selectedComponentIds as ListColumnField[])?.filter((a) => a != undefined && !excluded.includes(a.name)) || [];
      if (fields.length == 0) return;
      const isDeployed = list.stage != 'DRAFT';
      if (isDeployed) return;
      const dependenciesQuery = QueryBuilder.select()
        .from(CollectionNames.COMPONENT_DEPENDENCY)
        .where(and(eq('meta.list', list.name)))
        .build();
      const dependencies: ComponentDependency[] = await this.repository.findMany<ComponentDependency>(dependenciesQuery);
      for (const field of fields) {
        if (field.key) {
          MessageUtil.warning({
            summary: LanguageUtil.get({ id: 'common.label.key', defaultMessage: 'Key' }),
            detail: LanguageUtil.get({ id: 'messages.warning.variableCannotBeDeleted', defaultMessage: 'The variable selected as the key cannot be deleted!' }),
          });
          continue;
        }
        const dependency = dependencies.find((d) => d.dependentId == field.name);
        if (dependency) continue;
        const index = list.fields.indexOf(field);
        if (index != -1) {
          list.fields.splice(index, 1);
          selectedComponentIds.splice(selectedComponentIds.indexOf(field), 1);
        }
      }
      setSelectedItems(selectedComponentIds);
      changeList(list);
    }
  };
}

class State {
  name: string = '';
  properties: VariableProperties | undefined;
  key: boolean = false;
  description: string | undefined;
}

interface Props extends ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  list: state.list as ListState,
  component: state.component as ComponentState,
  user: state.auth.user as User,
});
const mapDispatchToProps = { changeList, changeListIsUpdate, changeListData, setSelectedItems };
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ListVariableCreatorComponent);
